import React, { useState, useEffect, useCallback } from 'react';
import { clustersAPI, serversAPI } from '../../services/api';
import ActionsMenu, { MENU_ITEM } from '../../components/ActionsMenu';

function StatusBadge({ status, style = {} }) {
  const colors = {
    ready: 'var(--color-success-500)', Ready: 'var(--color-success-500)',
    running: 'var(--color-success-500)', Running: 'var(--color-success-500)',
    active: 'var(--color-success-500)',
    pending: 'var(--color-warning-500)', Pending: 'var(--color-warning-500)',
    notready: 'var(--color-danger-500)', NotReady: 'var(--color-danger-500)',
    failed: 'var(--color-danger-500)', Failed: 'var(--color-danger-500)',
    provisioning: 'var(--brand)', connecting: 'var(--accent)',
    installing: 'var(--accent)', succeeded: 'var(--color-success-500)', Succeeded: 'var(--color-success-500)',
  };
  const bg = colors[status] || 'var(--muted)';
  return (
    <span className="status-badge" style={{ background: bg, color: 'white', ...style }}>
      {status}
    </span>
  );
}

// ── Node management view (imported clusters — SSH key management + auto-discovery) ──

export function NodeManagementView({ cluster, clusterId }) {
  const [nodes, setNodes]               = useState([]);
  const [syncing, setSyncing]           = useState(false);
  const [syncMsg, setSyncMsg]           = useState(null);

  // Cluster SSH form
  const [clusterSsh, setClusterSsh]     = useState({
    ssh_user: cluster.ssh_user || '',
    ssh_port: cluster.ssh_port || 22,
    ssh_private_key: cluster.ssh_private_key || '',
  });
  const [savingCluster, setSavingCluster] = useState(false);
  const [clusterSshMsg, setClusterSshMsg] = useState(null);

  // Per-node SSH override
  const [expandedNode, setExpandedNode] = useState(null);
  const [nodeSshForm, setNodeSshForm]   = useState({});
  const [savingNode, setSavingNode]     = useState(false);
  const [nodeSshMsg, setNodeSshMsg]     = useState(null);

  const reloadNodes = useCallback(async () => {
    try {
      const res = await clustersAPI.listNodes(clusterId);
      setNodes(res.data);
    } catch (e) {
      console.error('Failed to reload nodes', e);
    }
  }, [clusterId]);

  useEffect(() => { reloadNodes(); }, [reloadNodes]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await clustersAPI.syncNodes(clusterId);
      setSyncMsg({ ok: true, text: res.data.message || 'Nodes synced successfully' });
      await reloadNodes();
    } catch (e) {
      setSyncMsg({ ok: false, text: e.response?.data?.detail || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveClusterSsh = async (e) => {
    e.preventDefault();
    setSavingCluster(true);
    setClusterSshMsg(null);
    try {
      await clustersAPI.updateClusterSsh(clusterId, {
        ssh_user: clusterSsh.ssh_user || null,
        ssh_port: clusterSsh.ssh_port ? parseInt(clusterSsh.ssh_port) : null,
        ssh_private_key: clusterSsh.ssh_private_key || null,
      });
      setClusterSshMsg({ ok: true, text: 'Cluster SSH defaults saved' });
    } catch (e) {
      setClusterSshMsg({ ok: false, text: e.response?.data?.detail || 'Save failed' });
    } finally {
      setSavingCluster(false);
    }
  };

  const openNodeOverride = (node) => {
    setExpandedNode(expandedNode === node.id ? null : node.id);
    setNodeSshForm({
      ssh_user: node.ssh_user || '',
      ssh_port: node.ssh_port || '',
      ssh_private_key: node.ssh_private_key || '',
    });
    setNodeSshMsg(null);
  };

  const handleSaveNodeSsh = async (e, nodeId) => {
    e.preventDefault();
    setSavingNode(true);
    setNodeSshMsg(null);
    try {
      await clustersAPI.updateNodeSsh(clusterId, nodeId, {
        ssh_user: nodeSshForm.ssh_user || null,
        ssh_port: nodeSshForm.ssh_port ? parseInt(nodeSshForm.ssh_port) : null,
        ssh_private_key: nodeSshForm.ssh_private_key || null,
      });
      setNodeSshMsg({ ok: true, text: 'Node SSH override saved' });
      await reloadNodes();
    } catch (e) {
      setNodeSshMsg({ ok: false, text: e.response?.data?.detail || 'Save failed' });
    } finally {
      setSavingNode(false);
    }
  };

  const handleClearNodeSsh = async (nodeId) => {
    if (!window.confirm('Remove SSH override? This node will fall back to cluster defaults.')) return;
    setSavingNode(true);
    setNodeSshMsg(null);
    try {
      await clustersAPI.updateNodeSsh(clusterId, nodeId, {
        ssh_user: null, ssh_port: null, ssh_private_key: null,
      });
      setNodeSshMsg({ ok: true, text: 'Override cleared — using cluster key' });
      await reloadNodes();
    } catch (e) {
      setNodeSshMsg({ ok: false, text: e.response?.data?.detail || 'Clear failed' });
    } finally {
      setSavingNode(false);
    }
  };

  const sshBadge = (node) => {
    if (node.has_node_ssh_key)    return { label: 'Node key',    color: 'var(--brand)' };
    if (node.has_server_ssh_key)  return { label: 'Server key',  color: 'var(--accent)' };
    if (node.has_cluster_ssh_key) return { label: 'Cluster key', color: 'var(--color-success-500)' };
    return { label: 'No key', color: 'var(--color-danger-500)' };
  };

  return (
    <>
      {/* Node Discovery */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ margin: 0 }}>Worker Nodes — SSH Management</h2>
            <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 13 }}>
              Auto-discover nodes from Kubernetes API, then configure SSH credentials for base-machine access.
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : '⟳ Sync Nodes from K8s'}
          </button>
        </div>

        {syncMsg && (
          <div className={`banner ${syncMsg.ok ? 'success' : 'danger'}`} style={{ marginBottom: '1rem' }}>
            {syncMsg.text}
          </div>
        )}

        {nodes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>
            No nodes discovered yet. Click "Sync Nodes from K8s" to auto-discover all nodes.
          </div>
        ) : (
          <div className="table-card">
          <table className="ds">
            <thead>
              <tr>
                <th>Node Name</th>
                <th>Role</th>
                <th>Internal IP</th>
                <th>OS</th>
                <th>Kubelet</th>
                <th>Server</th>
                <th>SSH Credential</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {nodes.map(node => {
                const badge = sshBadge(node);
                const isExpanded = expandedNode === node.id;
                return (
                  <React.Fragment key={node.id}>
                    <tr>
                      <td><code style={{ fontSize: 13 }}>{node.label}</code></td>
                      <td>
                        <span className={`role-badge role-${node.role?.toLowerCase()}`}>{node.role}</span>
                      </td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace' }}>
                        {node.internal_ip || node.ip_address || '—'}
                        {node.external_ip && node.external_ip !== node.internal_ip && (
                          <div style={{ color: 'var(--muted)', fontSize: 11 }}>ext: {node.external_ip}</div>
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{node.os_image || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--muted)' }}>{node.kubelet_version || '—'}</td>
                      <td style={{ fontSize: 12 }}>
                        {node.server_name
                          ? <span style={{ color: 'var(--accent-ink)' }}>{node.server_name}</span>
                          : <span style={{ color: 'var(--muted-2)' }}>—</span>
                        }
                      </td>
                      <td>
                        <span className="status-badge" style={{ background: badge.color, color: 'white' }}>
                          {badge.label}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => openNodeOverride(node)}
                        >
                          {isExpanded ? 'Close' : 'SSH Override'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} style={{ background: 'var(--surface-2)', padding: '1rem 1.5rem' }}>
                          <form onSubmit={(e) => handleSaveNodeSsh(e, node.id)}>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                              <div className="form-group" style={{ minWidth: 140, margin: 0 }}>
                                <label style={{ fontSize: 12 }}>SSH User</label>
                                <input
                                  className="input"
                                  type="text"
                                  placeholder={cluster.ssh_user || 'root'}
                                  value={nodeSshForm.ssh_user}
                                  onChange={e => setNodeSshForm({ ...nodeSshForm, ssh_user: e.target.value })}
                                />
                                {!node.ssh_user && <small style={{ color: 'var(--muted)' }}>inherits: {cluster.ssh_user || 'root'}</small>}
                              </div>
                              <div className="form-group" style={{ minWidth: 100, margin: 0 }}>
                                <label style={{ fontSize: 12 }}>SSH Port</label>
                                <input
                                  className="input"
                                  type="number"
                                  placeholder={cluster.ssh_port || '22'}
                                  value={nodeSshForm.ssh_port}
                                  onChange={e => setNodeSshForm({ ...nodeSshForm, ssh_port: e.target.value })}
                                />
                                {!node.ssh_port && <small style={{ color: 'var(--muted)' }}>inherits: {cluster.ssh_port || 22}</small>}
                              </div>
                              <div className="form-group" style={{ flex: 1, minWidth: 240, margin: 0 }}>
                                <label style={{ fontSize: 12 }}>
                                  SSH Private Key (PEM)
                                  {!node.ssh_private_key && (
                                    <span style={{ marginLeft: 8, color: 'var(--color-success-500)', fontWeight: 600, fontSize: 11 }}>
                                      ✓ using cluster key
                                    </span>
                                  )}
                                  {node.ssh_private_key && (
                                    <span style={{ marginLeft: 8, color: 'var(--brand)', fontWeight: 600, fontSize: 11 }}>
                                      ✓ override set
                                    </span>
                                  )}
                                </label>
                                <textarea
                                  className="textarea"
                                  placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;(leave blank to use cluster key)"
                                  value={nodeSshForm.ssh_private_key}
                                  onChange={e => setNodeSshForm({ ...nodeSshForm, ssh_private_key: e.target.value })}
                                  rows={4}
                                  style={{ fontFamily: 'monospace', width: '100%' }}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                              <button type="submit" className="btn btn-sm btn-primary" disabled={savingNode}>
                                {savingNode ? 'Saving...' : 'Save Override'}
                              </button>
                              {(node.ssh_user || node.ssh_port || node.ssh_private_key) && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => handleClearNodeSsh(node.id)}
                                  disabled={savingNode}
                                >
                                  Clear Override
                                </button>
                              )}
                            </div>
                            {nodeSshMsg && (
                              <div style={{ marginTop: 8, color: nodeSshMsg.ok ? 'var(--color-success-500)' : 'var(--color-danger-500)', fontSize: 13 }}>
                                {nodeSshMsg.text}
                              </div>
                            )}
                          </form>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Cluster-level SSH defaults */}
      <div className="card">
        <h2>Cluster SSH Defaults</h2>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: -8, marginBottom: '1rem' }}>
          These credentials apply to all nodes that don't have a per-node SSH override.
        </p>
        <form onSubmit={handleSaveClusterSsh}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div className="form-group" style={{ minWidth: 160 }}>
              <label>SSH User</label>
              <input
                type="text"
                placeholder="root"
                value={clusterSsh.ssh_user}
                onChange={e => setClusterSsh({ ...clusterSsh, ssh_user: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ minWidth: 100 }}>
              <label>SSH Port</label>
              <input
                type="number"
                placeholder="22"
                value={clusterSsh.ssh_port}
                onChange={e => setClusterSsh({ ...clusterSsh, ssh_port: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: 300 }}>
              <label>SSH Private Key (PEM) — applies to all nodes</label>
              <textarea
                className="textarea"
                placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;..."
                value={clusterSsh.ssh_private_key}
                onChange={e => setClusterSsh({ ...clusterSsh, ssh_private_key: e.target.value })}
                rows={6}
                style={{ fontFamily: 'monospace', width: '100%' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={savingCluster}>
              {savingCluster ? 'Saving...' : 'Save Cluster SSH Defaults'}
            </button>
            {clusterSshMsg && (
              <span style={{ color: clusterSshMsg.ok ? 'var(--color-success-500)' : 'var(--color-danger-500)', fontSize: 13 }}>
                {clusterSshMsg.text}
              </span>
            )}
          </div>
        </form>
      </div>
    </>
  );
}

// ── Provisioned cluster nodes view ─────────────────────────────────────────────

export function ProvisionedNodesView({ cluster, clusterId, onReload }) {
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [editingNode, setEditingNode]     = useState(null);
  const [servers, setServers]             = useState([]);
  const [nodeForm, setNodeForm]           = useState({ server_id: '', label: '', role: 'WORKER' });

  useEffect(() => {
    serversAPI.list().then(r => setServers(r.data)).catch(() => {});
  }, []);

  const openAdd = () => {
    setEditingNode(null);
    setNodeForm({ server_id: '', label: '', role: 'WORKER', ip_address: '', ssh_user: 'root', ssh_password: '', ssh_private_key: '', access_mode: 'auto' });
    setShowNodeModal(true);
  };

  const openEdit = (node) => {
    setEditingNode(node);
    setNodeForm({ server_id: node.server_id || '', label: node.label, role: node.role });
    setShowNodeModal(true);
  };

  const handleProvision = async (nodeId) => {
    if (!window.confirm('Provision and join this node to the cluster?')) return;
    try {
      await clustersAPI.provisionNode(clusterId, nodeId);
      onReload();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (nodeId) => {
    if (!window.confirm('Delete this node?')) return;
    try {
      await clustersAPI.deleteNode(clusterId, nodeId);
      onReload();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingNode) {
        await clustersAPI.updateNode(clusterId, editingNode.id, nodeForm);
        setShowNodeModal(false);
        onReload();
      } else {
        const addData = {
          label: nodeForm.label,
          role: nodeForm.role,
          ip_address: nodeForm.ip_address,
          ssh_user: nodeForm.ssh_user || 'root',
          ssh_password: nodeForm.ssh_password,
          ssh_private_key: nodeForm.ssh_private_key,
          access_mode: nodeForm.access_mode || 'auto',
        };
        if (nodeForm.server_id) addData.server_id = parseInt(nodeForm.server_id);
        const res = await clustersAPI.addNode(clusterId, addData);
        const newNodeId = res.data?.id;
        setShowNodeModal(false);

        if (nodeForm.ip_address && newNodeId) {
          if (window.confirm(`Node "${nodeForm.label}" added. Run kubeadm join now to add it to the K8s cluster?`)) {
            try {
              await clustersAPI.joinWorkerNode(clusterId, newNodeId, {
                access_mode: nodeForm.access_mode || 'auto',
                ssh_password: nodeForm.ssh_password,
                ssh_private_key: nodeForm.ssh_private_key,
              });
              alert(`Join task queued for ${nodeForm.label}. Check provisioning logs on the cluster detail page.`);
            } catch (je) {
              const jd = je.response?.data?.detail;
              alert('Join queued but check logs: ' + (typeof jd === 'string' ? jd : JSON.stringify(jd) || je.message));
            }
          }
        }
        onReload();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert('Failed: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail) || e.message));
    }
  };

  return (
    <>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Nodes ({cluster.nodes?.length || 0})</h2>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Node</button>
        </div>

        {cluster.nodes && cluster.nodes.length > 0 ? (
          <div className="table-card">
          <table className="ds">
            <thead>
              <tr>
                <th>Label</th><th>Role</th><th>IP Address</th>
                <th>Hostname</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cluster.nodes.map(node => (
                <tr key={node.id}>
                  <td>{node.label}</td>
                  <td><span className={`role-badge role-${node.role?.toLowerCase()}`}>{node.role}</span></td>
                  <td>{node.ip_address}</td>
                  <td>{node.hostname}</td>
                  <td>
                    <StatusBadge status={node.status} />
                    {node.status_message && (
                      <div style={{ fontSize: '0.85em', color: 'var(--muted)', marginTop: 4 }}>{node.status_message}</div>
                    )}
                  </td>
                  <td>
                    <ActionsMenu>
                      {/* Provision = bare-metal SSH install/join. Hide it for AUTO-DISCOVERED
                          nodes: these are live representations the agent created just to
                          register the cluster (e.g. the control-plane node auto-made when an
                          agent cluster has no node records) — they have no IP/SSH key and
                          already run K8s, so Provision could only ever fail 'connect to
                          master'. A user-ADDED node (auto_discovered=false) is a real
                          provisioning target and still shows the button. */}
                      {!node.auto_discovered
                        && ['pending', 'failed'].includes(node.status?.toLowerCase()) && (
                        <button style={MENU_ITEM} onClick={() => handleProvision(node.id)}>
                          {node.status?.toLowerCase() === 'failed' ? 'Retry Provision' : 'Provision'}
                        </button>
                      )}
                      <button style={MENU_ITEM} onClick={() => openEdit(node)}>Edit</button>
                      <button style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }} onClick={() => handleDelete(node.id)}>Delete</button>
                    </ActionsMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>
            No nodes added yet. Click "+ Add Node" to add your first node.
          </div>
        )}
      </div>

      {showNodeModal && (
        <div className="modal-overlay" onClick={() => setShowNodeModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingNode ? 'Edit Node' : 'Add Node'}</h2>
              <button className="modal-close" onClick={() => setShowNodeModal(false)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>Label *</label>
                <input type="text" value={nodeForm.label} onChange={e => setNodeForm({ ...nodeForm, label: e.target.value })} placeholder="e.g., gpu-worker1" required />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select value={nodeForm.role} onChange={e => setNodeForm({ ...nodeForm, role: e.target.value })} required>
                  <option value="WORKER">Worker</option>
                  <option value="CONTROL_PLANE">Control Plane</option>
                </select>
              </div>

              {!editingNode && (
                <>
                  <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink-2)', marginBottom: 10 }}>SSH Connection</div>
                    <div className="form-group">
                      <label>IP Address *</label>
                      <input type="text" value={nodeForm.ip_address} onChange={e => setNodeForm({ ...nodeForm, ip_address: e.target.value })} placeholder="10.0.140.156 or 103.x.x.x" required />
                    </div>
                    <div className="form-group">
                      <label>SSH User</label>
                      <input type="text" value={nodeForm.ssh_user} onChange={e => setNodeForm({ ...nodeForm, ssh_user: e.target.value })} placeholder="root" />
                    </div>
                    <div className="form-group">
                      <label>SSH Password</label>
                      <input type="password" value={nodeForm.ssh_password} onChange={e => setNodeForm({ ...nodeForm, ssh_password: e.target.value })} placeholder="If password auth" />
                    </div>
                    <div className="form-group">
                      <label>Access via</label>
                      <select value={nodeForm.access_mode} onChange={e => setNodeForm({ ...nodeForm, access_mode: e.target.value })}>
                        <option value="auto">Auto-detect</option>
                        <option value="direct">Direct SSH (public IP)</option>
                        <option value="via_master">Via Master (private IP — master as jump host)</option>
                      </select>
                      {nodeForm.access_mode === 'via_master' && (
                        <small style={{ color: 'var(--muted)' }}>SarvKube will SSH to master first, then to this worker. Use for GPU/private workers.</small>
                      )}
                    </div>
                  </div>
                  <div className="banner warning" style={{ marginBottom: 12 }}>
                    After adding, you'll be asked whether to run kubeadm join to add this node to the K8s cluster.
                  </div>
                </>
              )}

              {editingNode && (
                <div className="form-group">
                  <label>Server</label>
                  <select value={nodeForm.server_id} onChange={e => setNodeForm({ ...nodeForm, server_id: parseInt(e.target.value) })}>
                    <option value="">Select a server</option>
                    {servers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.ip_address})</option>)}
                  </select>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNodeModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingNode ? 'Update' : 'Add & Join Node'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
